import { Category } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { queryKeys } from '@/lib/queryKeys';
import { mdiPlus, mdiRefresh, mdiTag, mdiTagMultiple } from '@mdi/js';
import * as React from 'react';
import { CategoriesFilters } from './CategoriesFilters';
import { CategoriesTable } from './CategoriesTable';

export interface CategoriesListProps {
  onRowClick: (category: Category) => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  onCreateCategory: () => void;
  isDeleting: boolean;
}

export const CategoriesList: React.FC<CategoriesListProps> = ({
  onRowClick,
  onEditCategory,
  onDeleteCategory,
  onCreateCategory,
  isDeleting,
}) => {
  const { state, list, actions, isLoading } = usePaginatedList<
    Category,
    { enabled: string }
  >({
    queryKey: queryKeys.ADMIN_CATEGORIES,
    endpoint: '/admin/categories',
    initialFilters: { enabled: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      enabled: filters.enabled ? filters.enabled === 'true' : undefined,
      sortBy: sortBy as 'name' | 'createdAt',
      sortOrder,
    }),
  });

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center'>
            <Icon path={mdiTag} size={32} className='mr-3' />
            Categories
          </h1>
          <p className='text-gray-600 mt-2'>
            Manage business categories for classification
          </p>
        </div>
        <div className='flex items-center space-x-3'>
          <div className='text-sm text-gray-600'>
            <Icon path={mdiTagMultiple} size={16} className='mr-1 inline' />
            {list.total} total categories
          </div>
          <Button
            icon={mdiRefresh}
            color='light'
            title='Refresh'
            onClick={actions.refetch}
            loading={isLoading}
          />
          <Button
            label='Add Category'
            icon={mdiPlus}
            color='primary'
            onClick={onCreateCategory}
          />
        </div>
      </div>

      <CategoriesFilters
        searchTerm={state.search}
        selectedEnabled={state.filters.enabled}
        onSearchChange={actions.setSearch}
        onEnabledChange={value => actions.setFilter('enabled', value)}
        onClearFilters={actions.clearFilters}
      />

      <CategoriesTable
        categories={list.items}
        isLoading={isLoading}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSort={actions.setSort}
        onRowClick={onRowClick}
        onEditCategory={onEditCategory}
        onDeleteCategory={onDeleteCategory}
        isDeleting={isDeleting}
      />

      {list.totalPages > 1 && (
        <Pagination
          currentPage={list.currentPage}
          totalPages={list.totalPages}
          onPageChange={actions.setPage}
          totalItems={list.total}
          itemsPerPage={list.limit}
        />
      )}
    </div>
  );
};
